/**
 * Tasks: team work items on one Board, optionally tied to jflo things (Contexts).
 * Design: docs/task-system-design.md
 */

import type { TaskRepeat } from '../task-repeat';
import type { LiveChangeMessage } from './live';

/**
 * Every jflo thing a task can be about. `page` is any jflo URL (a report with filters, a feed page);
 * `url` is a web page outside jflo (bravehardy.com, a supplier's site), linked from the Chrome extension.
 */
export const TASK_CONTEXT_TYPES = [
  'product_batch',
  'product_batch_product',
  'product',
  'variant',
  'vendor',
  'supplier',
  'purchase_order',
  'collection',
  'conversation',
  'page',
  'url',
] as const;

export type TaskContextType = (typeof TASK_CONTEXT_TYPES)[number];

export function isTaskContextType(value: unknown): value is TaskContextType {
  return typeof value === 'string' && (TASK_CONTEXT_TYPES as readonly string[]).includes(value);
}

/** A reference to a Context: `id` is a uuid, except for `page` (path + query) and `url` (the full address). */
export interface TaskContextRef {
  type: TaskContextType;
  id: string;
}

export type TaskContextSource = 'page' | 'mention' | 'manual' | 'slack';

export interface TaskContext extends TaskContextRef {
  title: string;
  /** Where "open" goes in jflo. */
  url: string;
  /** Parent Context, so a batch page also lists tasks on its batch products. */
  parent: TaskContextRef | null;
  source: TaskContextSource;
}

export interface TaskColumn {
  id: string;
  name: string;
  position: number;
  is_done: boolean;
}

export interface TaskLabel {
  id: string;
  name: string;
  color: TaskLabelColor;
  position: number;
}

export const TASK_LABEL_COLORS = [
  'gray',
  'red',
  'orange',
  'amber',
  'green',
  'teal',
  'blue',
  'violet',
  'pink',
] as const;

export type TaskLabelColor = (typeof TASK_LABEL_COLORS)[number];

/** The bespoke fields a task type can turn on (T-331), in the order they show. */
export const TASK_TYPE_FIELDS = ['size', 'kind', 'prs'] as const;
export type TaskTypeField = (typeof TASK_TYPE_FIELDS)[number];

export const TASK_SIZES = ['XS', 'S', 'M', 'L', 'XL'] as const;
export type TaskSize = (typeof TASK_SIZES)[number];

export const TASK_KINDS = ['fix', 'performance', 'feature', 'chore', 'docs', 'refactor', 'ci'] as const;
export type TaskKind = (typeof TASK_KINDS)[number];

export const TASK_PR_STATUSES = ['draft', 'open', 'merged', 'closed'] as const;
export type TaskPrStatus = (typeof TASK_PR_STATUSES)[number];

/** A pull request on a task, set by a person or the task loop (the worker never reads GitHub). */
export interface TaskPr {
  url: string;
  status: TaskPrStatus;
}

/** A task type (T-331). Every task has one; the default (Task) can't be deleted. */
export interface TaskType {
  id: string;
  name: string;
  color: TaskLabelColor;
  position: number;
  is_default: boolean;
  /** The bespoke fields this type shows, in TASK_TYPE_FIELDS order. */
  fields: TaskTypeField[];
}

/** The types every team starts with. */
export const DEFAULT_TASK_TYPES: Array<Pick<TaskType, 'name' | 'color' | 'is_default' | 'fields'>> = [
  { name: 'Task', color: 'gray', is_default: true, fields: [] },
  { name: 'Development', color: 'blue', is_default: false, fields: ['size', 'kind', 'prs'] },
  { name: 'Products', color: 'green', is_default: false, fields: ['size'] },
  { name: 'Operations', color: 'amber', is_default: false, fields: [] },
];

/**
 * Waiting on (T-331): whose turn it is. A person, another task (until it's done), or the AI builder
 * (the task loop, on teams that turn it on).
 */
export type TaskWaitingRef = { target: 'user'; user_id: string } | { target: 'task'; task_id: string } | { target: 'ai' };

/** As a card carries it: a waited-on task with its number, title and whether it's done. */
export type TaskWaiting =
  | { target: 'user'; user_id: string }
  | { target: 'task'; task_id: string; number: number; title: string; done: boolean }
  | { target: 'ai' };

/** The AI builder's name wherever it shows. */
export const TASK_AI_BUILDER_NAME = 'AI builder';

export interface TaskMember {
  user_id: string;
  displayname: string | null;
  email: string;
  photourl: string | null;
  /** Short name set in Team (as in the Inbox), e.g. "andrew"; shown where room is tight. */
  handle?: string | null;
}

/** A task as it appears on the Board and in Tasks panels. */
export interface TaskCard {
  id: string;
  /** Per-team Task number, shown as T-42 (formatTaskNumber). */
  number: number;
  title: string;
  column_id: string;
  rank: string;
  assignee_ids: string[];
  label_ids: string[];
  /** Its type (T-331): never null, the team's default when none was picked. */
  type_id: string;
  /** Jev chose the type (and kind) when it was made, and nobody has confirmed or changed it yet. */
  type_suggested: boolean;
  /** Bespoke fields (T-331); shown when the type turns them on, kept when it doesn't. */
  size: TaskSize | null;
  kind: TaskKind | null;
  prs: TaskPr[];
  /** Whose turn it is (T-331), oldest first. */
  waiting_on: TaskWaiting[];
  /** The first image or video in its Attachments (T-331), or null. */
  cover_attachment_id: string | null;
  /** Who follows it (muted followers left out): the Board's Following view (T-295). */
  follower_ids: string[];
  /** `#tags` from the title, description and chat (lower case, no #). */
  tags: string[];
  contexts: TaskContext[];
  chat_count: number;
  attachment_count: number;
  created_by_user_id: string | null;
  /** Archived by hand, or auto-archived 14 days after reaching a done Column. */
  archived: boolean;
  done_at: string | null;
  /** Snoozed until then (T-50): off the Board and out of counts. A past time means it's back. */
  snoozed_until: string | null;
  /** Repeat (T-49): it comes back to the top of the first Column on each day this lands on. */
  repeat: TaskRepeat | null;
  /** When the Repeat next brings it back; null without one. */
  repeat_next_at: string | null;
  /** The AI summary (T-318), or null before the first one. */
  ai_summary: TaskAiSummary | null;
  /** When a newer summary is due (activity since the last one); null when it's up to date. */
  ai_summary_due_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * A task's AI summary (T-318, AI operation task_summary): two plain-English sentences for someone
 * who hasn't followed the task. Remade a minute after activity (shared/task-summary.ts).
 */
export interface TaskAiSummary {
  /** What the task is and where it belongs, in a tense that follows its state. */
  what: string;
  /** Where it stands and who it's waiting for. */
  status: string;
  /** When it was made (ISO). */
  made_at: string;
  /** The Column it was made for. */
  state: string;
  model: string;
  prompt_version: number;
  log_id: string | null;
  /** The task's activity count when it was made (task.ai_summary_seq): a later request with a lower count never replaces it. */
  activity_seq: number;
}

export type TaskEventKind =
  | 'created'
  | 'chat'
  | 'moved'
  | 'renamed'
  | 'described'
  | 'assigned'
  | 'unassigned'
  | 'context_added'
  | 'context_removed'
  | 'label_added'
  | 'label_removed'
  | 'archived'
  | 'unarchived'
  | 'snoozed'
  | 'unsnoozed'
  /** Someone set, changed or removed its Repeat: payload `{ repeat: TaskRepeat | null }`. */
  | 'repeat_set'
  /** Its Repeat brought it back: payload `{ reopened, from, to, next_at }` (no actor). */
  | 'repeated'
  /** Its type changed (T-331): payload `{ from, to, from_name, to_name, suggested? }`; Jev's suggestion has no actor. */
  | 'type_set'
  /** A bespoke field changed: payload `{ field, from, to }` (prs: the lists). */
  | 'field_set'
  /** Someone is waited on now: payload `{ waiting: TaskWaitingRef, name }`. */
  | 'waiting_added'
  /**
   * A wait ended: payload `{ waiting, name, reason }`, reason `replied` (they commented), `done` (the
   * waited-on task finished), `review` (moved to Review, for the AI builder) or `removed` (by hand,
   * or replaced).
   */
  | 'waiting_removed';

export interface TaskAttachment {
  id: string;
  filename: string;
  content_type: string;
  size: number;
  /** Signed URL, valid for about an hour. */
  url: string;
  is_image: boolean;
  created_at: string;
  /** A square from its top-left corner (T-331), for images; null for other files. Signed like `url`. */
  square_url?: string | null;
}

export interface TaskEvent {
  id: string;
  kind: TaskEventKind;
  actor_user_id: string | null;
  /** Set when the actor's API key made this: shown as "Andrew (via Claude key)". */
  via_api_key_name: string | null;
  /** Chat text (with Mention markup), or null for system lines. */
  body: string | null;
  /** Kind-specific details, e.g. { from_column, to_column } or { context }. */
  payload: Record<string, unknown>;
  attachments: TaskAttachment[];
  created_at: string;
  edited_at: string | null;
}

export interface TaskDetail extends TaskCard {
  description: string;
  /**
   * The description's version (ISO): an edit sends it back as `expected_updated_at`, and a
   * description someone else saved since is refused (409 TASK_DESCRIPTION_STALE, T-214).
   */
  description_updated_at: string;
  /** True when the signed-in user follows this task and hasn't muted it. */
  following: boolean;
  events: TaskEvent[];
  /** In Attachments order (T-331): the first image or video is the cover. */
  attachments: TaskAttachment[];
}

export interface TaskBoard {
  columns: TaskColumn[];
  labels: TaskLabel[];
  /** The team's task types, in order (T-331). */
  types: TaskType[];
  /** Whether the AI builder can be picked in Waiting on. */
  ai_builder: boolean;
  members: TaskMember[];
  tasks: TaskCard[];
  /** Every tag in use on the team's tasks (archived ones too), most used first: the `#` picker and Tags filter. */
  tags: TaskTagCount[];
  /**
   * The Board loads only the latest finished tasks (TASK_BOARD_DONE_LIMIT, more after Show older;
   * T-377): how many more there are. Absent when every finished task is loaded (a Tasks panel's
   * board, the API).
   */
  older_done?: TaskOlderDone;
}

/** Done tasks (archived ones too, in the Archived view) the Board hasn't loaded. */
export interface TaskOlderDone {
  remaining: number;
}

/** Older finished tasks a Board search finds (T-377), newest finished first. */
export interface TaskFinishedPage {
  tasks: TaskCard[];
  /** More matched than were sent: the search shows only the latest. */
  more: boolean;
}

/** The Board's search sends at most this many older finished tasks. */
export const TASK_FINISHED_SEARCH_LIMIT = 50;

/** Most finished tasks the Board loads at once; Show older stops there. */
export const TASK_BOARD_DONE_MAX = 1000;

/** How many finished tasks the Board loads, newest first; Show older loads as many again (T-377). */
export const TASK_BOARD_DONE_LIMIT = 30;

export interface TaskTagCount {
  tag: string;
  count: number;
}

/** Board filters, also stored in Saved views as a query string. */
export interface TaskFilters {
  assignee?: string[];
  label?: string[];
  context_type?: TaskContextType[];
  q?: string;
  include_archived?: boolean;
}

export interface TaskCreateInput {
  /** Optional: chosen by the browser so the task can be shown and opened before the server answers. */
  id?: string;
  title: string;
  description?: string;
  column_id?: string;
  assignee_ids?: string[];
  label_ids?: string[];
  contexts?: Array<TaskContextRef & { title?: string }>;
  /** Left out, Jev suggests one after the task is made (T-331). */
  type_id?: string;
  size?: TaskSize | null;
  kind?: TaskKind | null;
  waiting_on?: TaskWaitingRef[];
}

export interface TaskUpdateInput {
  title?: string;
  description?: string;
  /**
   * With `description`: the description's version the edit started from (TaskDetail's
   * `description_updated_at`). A description changed since is refused with 409
   * TASK_DESCRIPTION_STALE and nothing is saved. Left out, nothing is checked.
   */
  expected_updated_at?: string | null;
  assignee_ids?: string[];
  label_ids?: string[];
  archived?: boolean;
  /** Snooze until this ISO time, or null to bring it back now. */
  snoozed_until?: string | null;
  /** Set or change its Repeat, or null to stop it. */
  repeat?: TaskRepeat | null;
  /** Its type; changing it, or the kind, clears Jev's "suggested" mark. */
  type_id?: string;
  /** Keep Jev's suggested type and kind: clears the mark without changing them. */
  confirm_type?: boolean;
  size?: TaskSize | null;
  kind?: TaskKind | null;
  prs?: TaskPr[];
  /** The whole new list; anyone left out stops being waited on. */
  waiting_on?: TaskWaitingRef[];
}

/** PATCH /api/tasks/:id: the card, and the description's new version when the save changed it. */
export interface TaskUpdateResponse {
  task: TaskCard | null;
  description_updated_at?: string;
}

export interface TaskMoveInput {
  column_id: string;
  /** Neighbours after the move (the card above and below); the server ranks it between them. */
  prev_task_id?: string | null;
  next_task_id?: string | null;
}

export interface TaskContextSearchHit extends TaskContextRef {
  title: string;
  subtitle: string | null;
}

/** A task in the top-bar search. */
export interface TaskSearchHit {
  id: string;
  number: number;
  title: string;
  column_name: string;
  is_done: boolean;
  archived: boolean;
  /** First linked thing, for a hint under the title. */
  context_title: string | null;
}

export interface TaskNotification {
  id: string;
  task_id: string;
  task_title: string;
  /** The task's number (T-42 is 42). */
  task_number: number;
  /**
   * `moved`: to Done. `review`: moved to Review. `unsnoozed`: its snooze ended and it's back on the
   * Board. `repeated`: its Repeat brought it back. `waiting`: it's your turn (T-331). `replied`: someone
   * it was waiting on commented. `unblocked`: a task it was waiting on is done (the excerpt names it).
   */
  kind: 'assigned' | 'mentioned' | 'chat' | 'moved' | 'review' | 'unsnoozed' | 'repeated' | 'waiting' | 'replied' | 'unblocked';
  actor_user_id: string | null;
  actor_name: string | null;
  /** Set when the actor's API key made the change. */
  via_api_key_name: string | null;
  excerpt: string | null;
  read: boolean;
  created_at: string;
}

/** The close code of a live socket refused because the member can't see tasks: don't reconnect (T-339). */
export const TASK_LIVE_FORBIDDEN = 4403;

/**
 * Messages the per-team live socket pushes to open pages. Mostly tasks, but other team-wide
 * changes ride along (`product_content`: the queued Shopify content changed; `inbox`: the shared inbox changed).
 */
/** Where someone's reply to a Conversation stands, for "Sam is replying…" and "Sam has a draft reply". */
export type InboxReplyPresence = 'typing' | 'draft' | 'closed';

export type TaskLiveMessage =
  /** `summary`: only its AI summary changed, which sends no task webhook (T-40). */
  | { type: 'task'; task_id: string; actor_user_id: string | null; summary?: true }
  | { type: 'task_deleted'; task_id: string }
  | { type: 'board'; actor_user_id: string | null }
  | { type: 'notification' }
  | { type: 'product_content' }
  /** The shared inbox changed (a sync, or someone acted on a Conversation). */
  | { type: 'inbox'; conversation_id?: string }
  /**
   * Someone's reply box in jflo: `typing` every few seconds while they type, `draft` every 20
   * seconds while the box is open, `closed` when it closes. No state means typing (older clients).
   */
  | { type: 'inbox_typing'; conversation_id: string; user_id: string; state?: InboxReplyPresence }
  /** Someone has this Conversation open (sent every 20 seconds while they do). */
  | { type: 'inbox_viewing'; conversation_id: string; user_id: string }
  /** Someone has this newsletter open to edit (T-382); sent every 20 seconds while they do. */
  | { type: 'newsletter_editing'; newsletter_id: string; user_id: string; name: string | null }
  | { type: 'landing_page_editing'; landing_page_id: string; user_id: string; name: string | null }
  /** Synced data or a record changed (`shared/types/live.ts`). */
  | LiveChangeMessage;

export interface TaskSlackStatus {
  configured: boolean;
  installed: boolean;
  slack_team_name: string | null;
  linked: boolean;
  dm_enabled: boolean;
}

/** Days a task sits in a done Column before it leaves the Board. */
export const TASK_AUTO_ARCHIVE_DAYS = 14;

/** Longest snooze. */
export const TASK_SNOOZE_MAX_DAYS = 366;

export const TASK_TITLE_MAX = 300;
export const TASK_DESCRIPTION_MAX = 20000;
export const TASK_CHAT_MAX = 10000;
export const TASK_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

export const DEFAULT_TASK_COLUMNS: Array<Pick<TaskColumn, 'name' | 'is_done'>> = [
  { name: 'To do', is_done: false },
  { name: 'Doing', is_done: false },
  { name: 'Waiting', is_done: false },
  { name: 'Done', is_done: true },
];

/**
 * The task webhook to software-factory (T-40), for Board settings: its last success and failure.
 * null when the team has none (another team's, or the AI builder off).
 */
export interface TaskWebhookStatus {
  last_sent_at: string | null;
  /** The latest failed send, kept after a later success: an HTTP status, "timeout" or "network error". */
  last_failure: { at: string; status: string } | null;
}
